'use client'

import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@creanizer/ui'
import { Button } from '@creanizer/ui'
import { Input } from '@creanizer/ui'
import { Textarea } from '@creanizer/ui'
import { Label } from '@creanizer/ui'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@creanizer/ui'
import { Save, X } from 'lucide-react'
import type { CharacterResult } from '@creanizer/types'

interface CharacterEditModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: (updatedResult: Partial<CharacterResult>) => void
  characterResult: CharacterResult
  title?: string
}

export function CharacterEditModal({
  isOpen,
  onClose,
  onSave,
  characterResult,
  title = '編集'
}: CharacterEditModalProps) {
  const [editedName, setEditedName] = useState(characterResult.name)
  const [editedAge, setEditedAge] = useState(characterResult.age)
  const [editedGender, setEditedGender] = useState(characterResult.gender)
  const [editedRole, setEditedRole] = useState(characterResult.role || '')
  const [editedOccupation, setEditedOccupation] = useState(characterResult.background?.occupation || '')
  const [editedSummary, setEditedSummary] = useState(characterResult.summary || '')
  const [editedBirthplace, setEditedBirthplace] = useState(characterResult.background?.birthplace || '')
  const [editedFamily, setEditedFamily] = useState(characterResult.background?.family || '')
  const [editedEducation, setEditedEducation] = useState(characterResult.background?.education || '')
  const [editedAbilities, setEditedAbilities] = useState((characterResult.background?.abilities || []).join('\n'))
  const [editedCombatStyle, setEditedCombatStyle] = useState((characterResult.behavior?.combatStyle || []).join('\n'))

  // characterResultが変更されたら編集中の値をリセット
  useEffect(() => {
    setEditedName(characterResult.name)
    setEditedAge(characterResult.age)
    setEditedGender(characterResult.gender)
    setEditedRole(characterResult.role || '')
    setEditedOccupation(characterResult.background?.occupation || '')
    setEditedSummary(characterResult.summary || '')
    setEditedBirthplace(characterResult.background?.birthplace || '')
    setEditedFamily(characterResult.background?.family || '')
    setEditedEducation(characterResult.background?.education || '')
    setEditedAbilities((characterResult.background?.abilities || []).join('\n'))
    setEditedCombatStyle((characterResult.behavior?.combatStyle || []).join('\n'))
  }, [characterResult])

  const handleSave = () => {
    onSave({
      name: editedName,
      age: editedAge,
      gender: editedGender,
      role: editedRole,
      summary: editedSummary,
      background: {
        ...characterResult.background,
        birthplace: editedBirthplace,
        family: editedFamily,
        education: editedEducation,
        occupation: editedOccupation,
        abilities: editedAbilities.split('\n').map(s => s.trim()).filter(s => s)
      },
      behavior: {
        ...characterResult.behavior,
        combatStyle: editedCombatStyle.split('\n').map(s => s.trim()).filter(s => s)
      }
    })
    onClose()
  }

  const handleCancel = () => {
    // 編集内容をリセット
    setEditedName(characterResult.name)
    setEditedAge(characterResult.age)
    setEditedGender(characterResult.gender)
    setEditedRole(characterResult.role || '')
    setEditedOccupation(characterResult.background?.occupation || '')
    setEditedSummary(characterResult.summary || '')
    setEditedBirthplace(characterResult.background?.birthplace || '')
    setEditedFamily(characterResult.background?.family || '')
    setEditedEducation(characterResult.background?.education || '')
    setEditedAbilities((characterResult.background?.abilities || []).join('\n'))
    setEditedCombatStyle((characterResult.behavior?.combatStyle || []).join('\n'))
    onClose()
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="name">名前</Label>
              <Input
                id="name"
                value={editedName}
                onChange={(e) => setEditedName(e.target.value)}
                placeholder="キャラクター名"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="age">年齢</Label>
              <Input
                id="age"
                value={editedAge}
                onChange={(e) => setEditedAge(e.target.value)}
                placeholder="年齢"
                className="mt-1"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="gender">性別</Label>
              <Select value={editedGender} onValueChange={setEditedGender}>
                <SelectTrigger id="gender" className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="男性">男性</SelectItem>
                  <SelectItem value="女性">女性</SelectItem>
                  <SelectItem value="その他">その他</SelectItem>
                  <SelectItem value="不明">不明</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="role">役割</Label>
              <Input
                id="role"
                value={editedRole}
                onChange={(e) => setEditedRole(e.target.value)}
                placeholder="役割"
                className="mt-1"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="summary">概要</Label>
            <Textarea
              id="summary"
              value={editedSummary}
              onChange={(e) => setEditedSummary(e.target.value)}
              placeholder="キャラクターの概要"
              rows={3}
              className="mt-1"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="birthplace">出身地</Label>
              <Input
                id="birthplace"
                value={editedBirthplace}
                onChange={(e) => setEditedBirthplace(e.target.value)}
                placeholder="出身地"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="occupation">職業</Label>
              <Input
                id="occupation"
                value={editedOccupation}
                onChange={(e) => setEditedOccupation(e.target.value)}
                placeholder="職業"
                className="mt-1"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="family">家族構成</Label>
            <Textarea
              id="family"
              value={editedFamily}
              onChange={(e) => setEditedFamily(e.target.value)}
              placeholder="家族構成"
              rows={2}
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="education">教育・訓練</Label>
            <Textarea
              id="education"
              value={editedEducation}
              onChange={(e) => setEditedEducation(e.target.value)}
              placeholder="教育・訓練の背景"
              rows={2}
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="abilities">能力・スキル（1行につき1項目）</Label>
            <Textarea
              id="abilities"
              value={editedAbilities}
              onChange={(e) => setEditedAbilities(e.target.value)}
              placeholder="料理が得意&#10;剣術の心得がある&#10;交渉術に長けている"
              rows={3}
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="combatStyle">武器・戦闘スタイル（1行につき1項目）</Label>
            <Textarea
              id="combatStyle"
              value={editedCombatStyle}
              onChange={(e) => setEditedCombatStyle(e.target.value)}
              placeholder="片手剣と盾を使用&#10;遠距離から魔法で攻撃&#10;危険時は逃げることを優先"
              rows={3}
              className="mt-1"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            <X className="h-4 w-4 mr-2" />
            キャンセル
          </Button>
          <Button onClick={handleSave}>
            <Save className="h-4 w-4 mr-2" />
            保存
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}